<template>
    <span class="tag-chip" :class="size" :style="style">
        <span class="dot" v-if="!hasBg" :style="{ background: color }"></span>
        <span class="name">{{ tag.name }}</span>
        <button
            v-if="removable"
            type="button"
            class="remove"
            aria-label="Retirer"
            @click.stop.prevent="emit('remove')">
            <FeatherIcon name="x" />
        </button>
    </span>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { Tag } from '@ezio/features/tags/types'
import FeatherIcon from '@ezio/ui/graphics/FeatherIcon.vue'

const props = withDefaults(
    defineProps<{
        tag: Tag
        removable?: boolean
        size?: 'sm' | 'md'
        filled?: boolean
    }>(),
    {
        removable: false,
        size: 'md',
        filled: true
    }
)

const emit = defineEmits<{ (e: 'remove'): void }>()

const color = computed(() => props.tag.color || '#64748b')
const hasBg = computed(() => props.filled)

const isLight = (hex: string): boolean => {
    const c = hex.replace('#', '')
    if (c.length !== 6) return false
    const r = parseInt(c.substring(0, 2), 16)
    const g = parseInt(c.substring(2, 4), 16)
    const b = parseInt(c.substring(4, 6), 16)
    return 0.299 * r + 0.587 * g + 0.114 * b > 160
}

const style = computed(() => {
    if (!props.filled) return {}
    return {
        background: color.value,
        color: isLight(color.value) ? '#1e293b' : '#ffffff'
    }
})
</script>

<style lang="scss" scoped>
.tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 999px;
    font-family: 'Sora', sans-serif;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    max-width: 100%;

    .name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    .remove {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: 0;
        padding: 0;
        margin: 0;
        cursor: pointer;
        color: inherit;
        opacity: 0.7;

        :deep(.feather) {
            width: 12px;
            height: 12px;
        }

        &:hover {
            opacity: 1;
        }
    }

    &.sm {
        font-size: 0.7rem;
        padding: 2px 8px;
        gap: 3px;

        .dot {
            width: 6px;
            height: 6px;
        }
    }

    &.md {
        font-size: 0.8rem;
        padding: 4px 10px;
    }
}
</style>
